import { memo, type FC } from 'react';
import { useLiveDuration } from '../../hooks/use-timeseries-lod';

export interface StatisticsGridProps {
  currentCount: number;
  weight: number;
  weightPerFish: number;
  tonnesPerHour: number;
  fishPerHour: number;
  sessionStart?: number | null;
  isCounting?: boolean;
  isMobile: boolean;
  isTablet: boolean;
}

/**
 * LiveDurationCell - Isolated component for duration display
 * Only this cell re-renders every second, not the entire grid
 */
const LiveDurationCell: FC<{
  sessionStart: number | null;
  isCounting: boolean;
  isMobile: boolean;
}> = memo(({ sessionStart, isCounting, isMobile }) => {
  const duration = useLiveDuration(sessionStart, isCounting);
  return (
    <span
      className="text-fs-text-primary font-semibold"
      style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
    >
      {duration}
    </span>
  );
});

/**
 * StatisticsGrid - Displays counter statistics in a 2-column grid layout
 * Shows Count, Weight, Duration, Weight per fish, Fish per hour, and Tonnes per hour
 */
export const StatisticsGrid: FC<StatisticsGridProps> = memo(({
  currentCount,
  weight,
  weightPerFish,
  tonnesPerHour,
  fishPerHour,
  sessionStart = null,
  isCounting = false,
  isMobile,
  isTablet,
}) => {
  return (
    <div
      className="grid grid-cols-2 gap-x-4"
      style={{
        flex: '0 0 auto',
        padding: isMobile
          ? 'clamp(8px, 2.5vw, 16px) clamp(4px, 3vw, 20px)'
          : isTablet
            ? '6px 12px 8px 12px'
            : 'clamp(8px, 1vw, 16px) clamp(16px, 2vw, 32px)',
        rowGap: isMobile ? 'clamp(6px, 2vw, 12px)' : isTablet ? '4px' : 'clamp(8px, 1vw, 16px)',
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Count
        </span>
        <span
          className="text-fs-text-primary font-semibold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          {Math.floor(currentCount).toLocaleString()}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Weight
        </span>
        <span
          className="text-fs-text-primary font-semibold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          {(() => {
            const floored = Math.floor(weight * 100) / 100;
            return floored.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          })()}{' '}
          t
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Duration
        </span>
        <LiveDurationCell
          sessionStart={sessionStart}
          isCounting={isCounting}
          isMobile={isMobile}
        />
      </div>
      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Weight per fish
        </span>
        <span
          className="text-fs-text-primary font-semibold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          {weightPerFish.toFixed(2)} kg
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Fish per hour
        </span>
        <span
          className="text-fs-text-primary font-semibold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          {Math.round(fishPerHour).toLocaleString()}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span
          className="text-fs-text-secondary font-bold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          Tonnes per hour
        </span>
        <span
          className="text-fs-text-primary font-semibold"
          style={{ fontSize: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(12px, 1.2vw, 18px)' }}
        >
          {(() => {
            const floored = Math.floor(tonnesPerHour * 100) / 100;
            return floored.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          })()}{' '}
          t
        </span>
      </div>
    </div>
  );
});


